import React, { useState } from "react";
import { getLocale } from "egov-ui-kit/utils/localStorageUtils";
import { formatINR, sumField, safeArray } from "egov-ui-kit/utils/commons";

const LEVELS = [
  { key: "ulbs", label: "ULB Name", childKey: "zones" },
  { key: "zones", label: "Zone Name", childKey: "departments" },
  { key: "departments", label: "Department Name", childKey: "funds", showCount: false },
  { key: "funds", label: "Fund Name", childKey: "heads", showCount: false },
  {
    key: "headCodeDetails",
    label: "Account Name",
    childKey: null, // last level -> clicking Count/Value opens Work Order detail
    extraColumns: [{ header: "Account Code", field: "accountHead" }],
  },
];

const formatDate = (d) => {
  if (!d) return "-";
  const dateObj = new Date(d);
  if (isNaN(dateObj.getTime())) return d;
  return dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const normalizeFundList = (rawList) =>
  safeArray(rawList).map((f, idx) => ({
    id: f.fundName || `fund_${idx}`,
    name: f.fundName,
    tenantId: f.tenantId,
    totalWorkOrder: f.totalWorkOrderAmount,
    totalNoOfWorkOrder: sumField(safeArray(f.headCodeDetails), "totalWorkOrders"),
    headCodeDetails: safeArray(f.headCodeDetails),
  }));

const normalizeHeadCodeList = (rawList) =>
  safeArray(rawList).map((h, idx) => ({
    id: h.headCode || `head_${idx}`,
    name: h.headName,
    accountHead: h.headCode,
    totalWorkOrder: h.totalAmount,
    totalNoOfWorkOrder: h.totalWorkOrders,
    workOrders: safeArray(h.workOrders),
  }));

const ReceiptDetailTable = ({ item, financialYear, ulbName, onBack }) => {
  const receipts = safeArray(item.workOrders);
  const totalAmount = sumField(receipts, "orderValue");
  const totalCount = receipts.length;

  return (
    <div className="receipt-card">
      {/* HEADER */}
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          <button className="btn btn-secondary back-button" onClick={onBack}>
            Back
          </button>
          <div>
            <h2>{item.name}</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {financialYear}</span>
          <span>|</span>
          <span>ULB : {ulbName}</span>
        </div>
      </div>

      {/* DETAIL TABLE */}
      <div className="table-responsive">
        <table className="table receipt-table mb-0">
          <thead>
            <tr>
              <th>Work Order Date</th>
              <th>Work Order No.</th>
              <th>Work Order Value</th>
              <th>Contractor Name</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {receipts.length > 0 ? (
              <React.Fragment>
                {receipts.map((r, idx) => (
                  <tr key={r.workProposalNo ? `${r.workProposalNo}_${idx}` : idx}>
                    <td>{formatDate(r.orderDate)}</td>
                    <td>{r.workProposalNo}</td>
                    <td>{formatINR(r.orderValue)}</td>
                    <td>{r.contractorName}</td>
                    <td>
                      <span className={`status-badge ${(r.status || "").toLowerCase().replace(" ", "-")}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
                {/* <tr className="total-row">
                  <td colSpan={3}>Total ({totalCount})</td>
                  <td>{formatINR(totalAmount)}</td>
                </tr> */}
              </React.Fragment>
            ) : (
              <tr>
                <td colSpan="4" style={{ textAlign: "center" }}>
                  No Work Orders Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const WorkOrderTable = ({
  workOrderDetails,
  workOrderFundDetails, // fund-wise API response (fetched on department click)
  fetchWorkOrderFundDetails, // fn to trigger the fund-wise API call
  ulbType,
  fromDate,
  toDate,
}) => {
  const tableData = workOrderDetails && workOrderDetails;

  const [breadcrumb, setBreadcrumb] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null); // needed for fund payload's zoneName

  // —— Receipt detail view ————————————————————
  if (selectedReceipt) {
    return (
      <ReceiptDetailTable
        item={selectedReceipt}
        financialYear={tableData.financialYear}
        ulbName={tableData.ulbName}
        onBack={() => setSelectedReceipt(null)}
      />
    );
  }

  // —— Derived State ——————————————————————————
  const levelIndex = breadcrumb.length;
  const levelConfig = LEVELS[levelIndex];
  const isLastLevel = levelConfig.childKey === null;
  const isRootLevel = levelIndex === 0;
  const isDepartmentLevel = levelConfig.key === "departments";
  const isFundLevel = levelConfig.key === "funds";
  const extraColumns = levelConfig.extraColumns || [];
  const showCount = levelConfig.showCount !== false; // default true unless explicitly false

  const currentRows = (function () {
    if (isRootLevel) {
      return safeArray(tableData[LEVELS[0].key]);
    }

    var currentKey = LEVELS[levelIndex].key;
    var parentItem = breadcrumb[levelIndex - 1].item;

    if (currentKey === "funds") {
      return normalizeFundList(workOrderFundDetails);
    }

    if (currentKey === "headCodeDetails") {
      return normalizeHeadCodeList(parentItem.headCodeDetails);
    }

    return safeArray(parentItem[currentKey]);
  })();

  const currentTitle = isRootLevel ? "ULB Wise Work Order" : breadcrumb[levelIndex - 1].item.name;

  const currentTotalWorkOrder = sumField(currentRows, "totalNoOfWorkOrder");
  const currentTotal = sumField(currentRows, "totalWorkOrder");
  const tenantIds = [...new Set(currentRows.map((row) => row.tenantId).filter(Boolean))];

  // —— Handlers ———————————————————————————————
  const handleDrillDown = (item) => {
    if (isLastLevel) {
      setSelectedReceipt(item);
      return;
    }

    if (levelConfig.key === "zones") {
      setSelectedZone(item);
    }

    if (isDepartmentLevel) {
      const payload = {
        financialYear: tableData.financialYear,
        ulbName: breadcrumb[0] ? breadcrumb[0].item.tenantId : null,
        zoneName: selectedZone ? selectedZone.name : null,
        deptCode: item.deptCode || null,
        ulbType: ulbType,
        fromDate: fromDate,
        toDate: toDate,
        locale: getLocale(),
      };
      fetchWorkOrderFundDetails(payload);
    }

    setBreadcrumb(function (prev) {
      return prev.concat([{ levelIndex: levelIndex, item: item }]);
    });
  };

  const handleBack = () => {
    setBreadcrumb(function (prev) {
      var newBreadcrumb = prev.slice(0, -1);
      if (newBreadcrumb.length < 2) setSelectedZone(null);
      return newBreadcrumb;
    });
  };

  const handleBreadcrumbClick = (toIndex) => {
    setBreadcrumb(function (prev) {
      var newBreadcrumb = prev.slice(0, toIndex);
      if (newBreadcrumb.length < 2) setSelectedZone(null);
      return newBreadcrumb;
    });
  };

  const emptyMessage = isFundLevel ? "Loading fund details..." : "No Record Found";

  // —— Render —————————————————────────────────
  return (
    <div className="receipt-card">
      {/* HEADER */}
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          {isRootLevel ? (
            <div className="icon-wrapper">
              <i className="material-icons">description</i>
            </div>
          ) : (
            <button className="btn btn-secondary back-button" onClick={handleBack}>
              Back
            </button>
          )}
          <div>
            <h2>{currentTitle}</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {tableData.financialYear}</span>
          <span>|</span>
          <span>ULB : {tableData.ulbName}</span>
        </div>
      </div>

      {/* BREADCRUMB */}
      {!isRootLevel && (
        <div className="breadcrumb-bar">
          <span className="bc-link" onClick={() => handleBreadcrumbClick(0)} role="button" tabIndex={0}>
            ULB Wise Work Order
          </span>
          {breadcrumb.map(function (bc, idx) {
            var isLast = idx === breadcrumb.length - 1;
            return (
              <React.Fragment key={bc.item.id}>
                <span className="bc-sep"> › </span>
                {isLast ? (
                  <span className="bc-current">{bc.item.name}</span>
                ) : (
                  <span className="bc-link" onClick={() => handleBreadcrumbClick(idx + 1)} role="button" tabIndex={0}>
                    {bc.item.name}
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* SINGLE MAIN TABLE */}
      <div
        className={`table-responsive ${levelConfig.key === "ulbs" || levelConfig.key === "zones" || levelConfig.key === "departments" ? "scrolling-table" : ""}`}
      >
        <table className="table receipt-table mb-0">
          <thead>
            <tr>
              <th>{levelConfig.label}</th>
              {extraColumns.map(function (col) {
                return <th key={col.field}>{col.header}</th>;
              })}
              {showCount && <th>Total No. of Work Orders</th>}
              <th>Total Work Order Value</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              <React.Fragment>
                {currentRows.map(function (item) {
                  return (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      {extraColumns.map(function (col) {
                        return <td key={col.field}>{item[col.field]}</td>;
                      })}
                      {showCount && (
                        <td>
                          <a
                            href="#"
                            className="link-cell"
                            onClick={function (e) {
                              e.preventDefault();
                              handleDrillDown(item);
                            }}
                          >
                            {formatINR(item.totalNoOfWorkOrder)}
                          </a>
                        </td>
                      )}
                      <td>
                        <a
                          href="#"
                          className="link-cell"
                          onClick={function (e) {
                            e.preventDefault();
                            handleDrillDown(item);
                          }}
                        >
                          {formatINR(item.totalWorkOrder)}
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ) : (
              <tr>
                <td colSpan={(showCount ? 3 : 2) + extraColumns.length} style={{ textAlign: "center" }}>
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>

          {currentRows.length > 0 && (
            <tfoot className="tfoot-bottom">
              <tr className="total-row">
                <td colSpan={1 + extraColumns.length} style={{ textAlign: "left" }}>
                  Total
                </td>
                {showCount && <td>{formatINR(currentTotalWorkOrder)}</td>}
                <td>{formatINR(currentTotal)}</td>
              </tr>
            </tfoot>
          )}

          <tr className="total-row"></tr>
        </table>
      </div>
    </div>
  );
};

export default WorkOrderTable;



// WEBPACK FOOTER //
// src/modules/employee/Home/WorkOrderTable.js